// src/components/CostCalculator.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import '../styles/CostCalculator.css';
import genieImage from '../assets/images/genie.jpg';

const FALLBACK_PACKAGE_OPTIONS = [
  { label: 'Basic Package @ 2199/sqft', rate: 2199 },
  { label: 'Standard Package @ 2399/sqft', rate: 2399 },
  { label: 'Premium Package @ 2599/sqft', rate: 2599 }
];

const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';

const CelebrationAnimation = () => (
  <motion.div
    className="celebration-overlay"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.9 }}
  >
    <div className="confetti-container">
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="confetti"
          initial={{ y: -10, opacity: 1 }}
          animate={{ y: '100vh', opacity: 0 }}
          transition={{ duration: Math.random() * 2 + 2, delay: Math.random() * 1 }}
          style={{
            left: `${Math.random() * 100}vw`,
            backgroundColor: ['#4169E1', '#FF4444', '#FFD700', '#00FF7F'][i % 4]
          }}
        />
      ))}
    </div>

    <motion.div
      className="celebration-message"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 200, damping: 10 }}
    >
      <motion.img
        src={genieImage}
        alt="Genie"
        className="genie-img"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.5, type: 'spring', stiffness: 150 }}
      />
      <motion.h3
        className="celebration-title"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.6 }}
      >
        Congratulations!
      </motion.h3>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.0, duration: 0.6 }}
      >
        Your construction estimate is ready!
      </motion.p>
      <motion.div
        className="celebration-sparkles"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.2, duration: 0.5 }}
      >
        ✨ ⭐ ✨
      </motion.div>
    </motion.div>
  </motion.div>
);

const CostCalculator = () => {
  const [packageOptions, setPackageOptions] = useState(FALLBACK_PACKAGE_OPTIONS);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    totalArea: '',
    floors: 'Ground',
    package: FALLBACK_PACKAGE_OPTIONS[0].label,
    groundFloorArea: '',
    firstFloorArea: '',
    secondFloorArea: '',
    thirdFloorArea: '',
    fourthFloorArea: '',
    waterSump: '',
    septicTank: '',
    wallLength: ''
  });
  const [errors, setErrors] = useState({ name: '', phone: '', email: '', location: '', totalArea: '' });
  const [submitted, setSubmitted] = useState(false);
  const [totalCost, setTotalCost] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get_packages.php`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();

        if (result.success && Array.isArray(result.packages) && result.packages.length > 0) {
          const toTitleCase = (str) => str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
          const options = result.packages.map(p => ({
            label: `${toTitleCase(p.title)} @ ${p.price}/sqft`,
            rate: parseInt(p.price, 10)
          }));
          setPackageOptions(options);
          setFormData(prev => ({ ...prev, package: options[0].label }));
        }
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      }
    };

    fetchPackages();
  }, []);

  const getRateForPackage = (packageLabel) => {
    const match = packageOptions.find(opt => opt.label === packageLabel);
    return match ? match.rate : packageOptions[0].rate;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isFormValid =
    formData.name.trim() !== '' &&
    /^\d{10}$/.test(formData.phone) &&
    isValidEmail(formData.email) &&
    formData.location.trim() !== '' &&
    formData.totalArea.trim() !== '';

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Field-specific validation
    if (field === 'email') {
      setErrors(prev => ({
        ...prev,
        email: value.trim() === '' ? 'Email required' : !isValidEmail(value) ? 'Invalid email format' : ''
      }));
    } else {
      setErrors(prev => ({ ...prev, [field]: value.trim() ? '' : `${field} required` }));
    }
  };

  const calculateCost = data => {
    let cost = 0;
    const rate = getRateForPackage(data.package);
    ['groundFloorArea', 'firstFloorArea', 'secondFloorArea', 'thirdFloorArea', 'fourthFloorArea'].forEach(k => {
      if (data[k]) cost += parseInt(data[k], 10) * rate;
    });
    if (data.waterSump) cost += parseInt(data.waterSump, 10) * 35;
    if (data.septicTank) cost += parseInt(data.septicTank, 10) * 20;
    if (data.wallLength) cost += parseInt(data.wallLength, 10) * 1850;
    return cost;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isFormValid) return;

    const cost = calculateCost(formData);
    setTotalCost(cost);
    setSubmitted(true);
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 3000);

    // Calculate built-up area for all floors
    const totalBuiltupArea = ['groundFloorArea', 'firstFloorArea', 'secondFloorArea', 'thirdFloorArea', 'fourthFloorArea']
      .reduce((acc, key) => acc + (formData[key] ? parseInt(formData[key], 10) : 0), 0);

    // Calculate built-up cost for all floors
    const rate = getRateForPackage(formData.package);
    const totalBuiltupCost = totalBuiltupArea * rate;

    // EmailJS sending logic
    const templateParams = {
      user_name: formData.name,
      user_phone: formData.phone,
      user_email: formData.email,
      user_location: formData.location,
      user_area: formData.totalArea,
      user_floors: formData.floors,
      user_package: formData.package,
      builtup_area: totalBuiltupArea.toString(),
      builtup_rate: `Rs.${rate}`,
      builtup_cost: `Rs.${totalBuiltupCost.toLocaleString()}`,
      sump_size: formData.waterSump || '0',
      sump_rate: 'Rs.35',
      sump_cost: `Rs.${formData.waterSump ? (parseInt(formData.waterSump, 10) * 35).toLocaleString() : '0'}`,
      septic_size: formData.septicTank || '0',
      septic_rate: 'Rs.20',
      septic_cost: `Rs.${formData.septicTank ? (parseInt(formData.septicTank, 10) * 20).toLocaleString() : '0'}`,
      compound_length: formData.wallLength || '0',
      compound_rate: 'Rs.1850',
      compound_cost: `Rs.${formData.wallLength ? (parseInt(formData.wallLength, 10) * 1850).toLocaleString() : '0'}`,
      total_estimate: `Rs.${cost.toLocaleString()}`
    };

    try {
      await emailjs.send(
        "service_62r2coo",
        "template_xwd2wy7",
        templateParams,
        "B3AZguJeCkvYjwzhh"
      );
    } catch (error) {
      console.error('EmailJS Error:', error);
    }
  };

  const floorMap = {
    Ground: ['ground'],
    'Ground + 1': ['ground', 'first'],
    'Ground + 2': ['ground', 'first', 'second'],
    'Ground + 3': ['ground', 'first', 'second', 'third'],
    'Ground + 4': ['ground', 'first', 'second', 'third', 'fourth']
  };
  const labels = {
    ground: 'Ground Floor',
    first: 'First Floor',
    second: 'Second Floor',
    third: 'Third Floor',
    fourth: 'Fourth Floor'
  };
  const fieldNames = {
    ground: 'groundFloorArea',
    first: 'firstFloorArea',
    second: 'secondFloorArea',
    third: 'thirdFloorArea',
    fourth: 'fourthFloorArea'
  };

  return (
    <section className="calculator-section">
      <div className="calculator-container">
        <motion.div
          className="calculator-header"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Home Construction Cost Calculator (2025) Chennai</h2>
          <p>Estimate your construction cost here</p>
        </motion.div>

        <motion.form
          className="calculator-form"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          onSubmit={handleSubmit}
        >
          <div className="form-controls">
            <div className="control-group">
              <label htmlFor="name">Name *</label>
              <input
                id="name"
                type="text"
                placeholder="Enter name"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
                required
              />
              {errors.name && <span className="error">{errors.name}</span>}
            </div>

            <div className="control-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                id="phone"
                type="tel"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={e => handleChange('phone', e.target.value)}
                required
                pattern="\d{10}"
                title="Please enter a 10-digit phone number"
              />
              {errors.phone && <span className="error">{errors.phone}</span>}
            </div>

            <div className="control-group">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={e => handleChange('email', e.target.value)}
                required
              />
              {errors.email && <span className="error">{errors.email}</span>}
            </div>

            <div className="control-group">
              <label htmlFor="location">Location *</label>
              <input
                id="location"
                type="text"
                placeholder="Enter location"
                value={formData.location}
                onChange={e => handleChange('location', e.target.value)}
                required
              />
              {errors.location && <span className="error">{errors.location}</span>}
            </div>

            <div className="control-group">
              <label htmlFor="totalArea">Total Land Area *</label>
              <input
                id="totalArea"
                type="number"
                placeholder="Enter total area"
                value={formData.totalArea}
                onChange={e => handleChange('totalArea', e.target.value)}
                required
              />
              {errors.totalArea && <span className="error">{errors.totalArea}</span>}
            </div>

            <div className="control-group">
              <label>No. of Floors *</label>
              <select value={formData.floors} onChange={e => handleChange('floors', e.target.value)}>
                {Object.keys(floorMap).map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <label>Package *</label>
              <select value={formData.package} onChange={e => handleChange('package', e.target.value)}>
                {packageOptions.map(opt => (
                  <option key={opt.label}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="calculator-table">
            <div className="table-header">
              <div>Work</div><div>Area</div><div>Unit</div><div>Rate</div><div>Cost</div>
            </div>

            {floorMap[formData.floors].map(f => (
              <div key={f} className="table-row">
                <div>Area for {labels[f]}</div>
                <div>
                  <input
                    type="number"
                    placeholder="Area in sqft"
                    value={formData[fieldNames[f]]}
                    onChange={e => handleChange(fieldNames[f], e.target.value)}
                  />
                </div>
                <div>sqft</div>
                <div>Rs.{getRateForPackage(formData.package)}</div>
                <div>
                  Rs.{formData[fieldNames[f]]
                    ? (parseInt(formData[fieldNames[f]], 10) * getRateForPackage(formData.package)).toLocaleString()
                    : 0}
                </div>
              </div>
            ))}

            {['waterSump', 'septicTank', 'wallLength'].map((f, i) => (
              <div key={i} className="table-row">
                <div>
                  {f === 'waterSump'
                    ? 'Size of RCC Water Sump (3000 litre)'
                    : f === 'septicTank'
                      ? 'Size of Septic Tank (10000 litre)'
                      : 'Compound Wall (Height 5ft)'}
                </div>
                <div>
                  <input
                    type="number"
                    placeholder="Value"
                    value={formData[f]}
                    onChange={e => handleChange(f, e.target.value)}
                  />
                </div>
                <div>{f === 'wallLength' ? 'RFT' : 'litre'}</div>
                <div>Rs.{f === 'waterSump' ? 35 : f === 'septicTank' ? 20 : 1850}</div>
                <div>
                  Rs.{formData[f]
                    ? (parseInt(formData[f], 10) * (f === 'waterSump' ? 35 : f === 'septicTank' ? 20 : 1850)).toLocaleString()
                    : 0}
                </div>
              </div>
            ))}

            {submitted && (
              <div className="table-total-centered">
                <div className="total-content">
                  <div className="total-label"><strong>Total Construction Cost</strong></div>
                  <motion.div
                    className="total-amount"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <strong>Rs. {totalCost.toLocaleString()}</strong>
                  </motion.div>
                </div>
              </div>
            )}
          </div>

          <motion.button
            type="submit"
            className="estimate-btn"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={!isFormValid}
          >
            GET FREE ESTIMATE NOW
          </motion.button>
        </motion.form>

        {showCelebration && <CelebrationAnimation />}
      </div>
    </section>
  );
};

export default CostCalculator;